import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Brush } from 'recharts';

interface IchimokuData {
  date: string;
  close: number;
  high: number;
  low: number;
  tenkanSen: number;
  kijunSen: number;
  senkouSpanA: number;
  senkouSpanB: number;
  chikouSpan: number;
}

interface IchimokuAlert {
  type: 'KUMO_BREAK_DOWN' | 'WITHIN_KUMO' | 'KUMO_EXIT_DOWN' | 'TENKAN_TOUCH_UP' | 'TENKAN_TOUCH_DOWN' | 'KIJUN_TOUCH_UP' | 'KIJUN_TOUCH_DOWN';
  description: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface IchimokuResult {
  ticker: string;
  timeframe: 'daily' | 'weekly';
  currentPrice: number;
  tenkanSen: number;
  kijunSen: number;
  senkouSpanA: number;
  senkouSpanB: number;
  chikouSpan: number;
  alerts: IchimokuAlert[];
  priceHistory: IchimokuData[];
  error?: string;
}

interface Props {
  result: IchimokuResult;
}

const alertTypeLabels: Record<string, { label: string; color: string }> = {
  'KUMO_BREAK_DOWN': { label: 'Kumo Break ↓', color: 'bg-red-500' },
  'WITHIN_KUMO': { label: 'In Kumo', color: 'bg-yellow-500' },
  'KUMO_EXIT_DOWN': { label: 'Kumo Exit ↓', color: 'bg-red-600' },
  'TENKAN_TOUCH_UP': { label: 'Tenkan ↑', color: 'bg-green-500' },
  'TENKAN_TOUCH_DOWN': { label: 'Tenkan ↓', color: 'bg-orange-500' },
  'KIJUN_TOUCH_UP': { label: 'Kijun ↑', color: 'bg-blue-500' },
  'KIJUN_TOUCH_DOWN': { label: 'Kijun ↓', color: 'bg-purple-500' },
};

export const IchimokuSurveillanceChart = ({ result }: Props) => {
  const [timeRange, setTimeRange] = useState<'1M' | '3M' | '6M' | '1Y' | 'ALL'>('3M');

  const chartData = useMemo(() => {
    if (!result?.priceHistory) return [];
    
    const data = result.priceHistory;
    let sliceCount = data.length;
    
    switch (timeRange) {
      case '1M': sliceCount = result.timeframe === 'weekly' ? 4 : 22; break;
      case '3M': sliceCount = result.timeframe === 'weekly' ? 13 : 66; break;
      case '6M': sliceCount = result.timeframe === 'weekly' ? 26 : 132; break;
      case '1Y': sliceCount = result.timeframe === 'weekly' ? 52 : 252; break;
      case 'ALL': sliceCount = data.length; break;
    }
    
    return data.slice(-Math.min(sliceCount, data.length));
  }, [result, timeRange]);

  const timeRangeButtons: ('1M' | '3M' | '6M' | '1Y' | 'ALL')[] = ['1M', '3M', '6M', '1Y', 'ALL'];

  return (
    <div className="space-y-4">
      {/* Time Range Selector */}
      <div className="flex justify-center gap-2">
        {timeRangeButtons.map((range) => (
          <Button
            key={range}
            variant={timeRange === range ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange(range)}
          >
            {range}
          </Button>
        ))}
      </div>

      {/* Alert Badges */}
      <div className="flex flex-wrap gap-2 justify-center">
        {result.alerts.map((alert, idx) => {
          const config = alertTypeLabels[alert.type];
          return (
            <Badge key={idx} className={`${config.color} text-white`}>
              {alert.description}
            </Badge>
          );
        })}
        {result.alerts.length === 0 && (
          <Badge variant="outline">No active alerts</Badge>
        )}
      </div>

      {/* Chart */}
      <div className="h-[500px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 10 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis 
              domain={['auto', 'auto']}
              tick={{ fontSize: 10 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              labelFormatter={(value) => new Date(value).toLocaleDateString()}
              formatter={(value: number, name: string) => [
                `$${value.toFixed(2)}`,
                name.replace(/([A-Z])/g, ' $1').trim()
              ]}
            />
            <Legend />
            
            {/* Kumo Cloud (area between Senkou Span A and B) */}
            <Area
              type="monotone"
              dataKey="senkouSpanA"
              stroke="none"
              fill="rgba(76, 175, 80, 0.15)"
              name="Senkou A"
            />
            <Area
              type="monotone"
              dataKey="senkouSpanB"
              stroke="none"
              fill="rgba(244, 67, 54, 0.15)"
              name="Senkou B"
            />
            
            {/* Senkou Span Lines */}
            <Line
              type="monotone"
              dataKey="senkouSpanA"
              stroke="#4CAF50"
              strokeWidth={1}
              dot={false}
              name="Senkou Span A"
            />
            <Line
              type="monotone"
              dataKey="senkouSpanB"
              stroke="#F44336"
              strokeWidth={1}
              dot={false}
              name="Senkou Span B"
            />
            
            {/* Tenkan-Sen (Conversion Line) */}
            <Line
              type="monotone"
              dataKey="tenkanSen"
              stroke="#2196F3"
              strokeWidth={1.5}
              dot={false}
              name="Tenkan-Sen"
            />
            
            {/* Kijun-Sen (Base Line) */}
            <Line
              type="monotone"
              dataKey="kijunSen"
              stroke="#9C27B0"
              strokeWidth={1.5}
              dot={false}
              name="Kijun-Sen"
            />
            
            {/* Price Line - Bold Yellow for visibility */}
            <Line
              type="monotone"
              dataKey="close"
              stroke="#FACC15"
              strokeWidth={3}
              dot={false}
              name="Price"
            />

            {/* Brush for zoom/scroll */}
            <Brush
              dataKey="date"
              height={30}
              stroke="hsl(var(--primary))"
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Ichimoku Values */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
        <Card className="bg-yellow-500/20 border-yellow-500/50">
          <CardContent className="p-2 text-center">
            <div className="text-yellow-600 text-xs font-medium">Price</div>
            <div className="font-bold text-yellow-600">${result.currentPrice}</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10">
          <CardContent className="p-2 text-center">
            <div className="text-blue-600 text-xs">Tenkan</div>
            <div className="font-semibold">${result.tenkanSen}</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10">
          <CardContent className="p-2 text-center">
            <div className="text-purple-600 text-xs">Kijun</div>
            <div className="font-semibold">${result.kijunSen}</div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10">
          <CardContent className="p-2 text-center">
            <div className="text-green-600 text-xs">Span A</div>
            <div className="font-semibold">${result.senkouSpanA}</div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10">
          <CardContent className="p-2 text-center">
            <div className="text-red-600 text-xs">Span B</div>
            <div className="font-semibold">${result.senkouSpanB}</div>
          </CardContent>
        </Card>
        <Card className="bg-muted">
          <CardContent className="p-2 text-center">
            <div className="text-muted-foreground text-xs">Chikou</div>
            <div className="font-semibold">${result.chikouSpan}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
